import { FC, useCallback, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useT } from '../../../../../../../_metronic/i18n/Metronici18n'
import { Button, InfoAlert, LoadingIndicator } from '../../../../../../components'
import { EstudoDicom, ResultadoSolicitacaoExameImagem } from '../../../../atendimento/core/_models'
import { useExamesImagemSocket } from '../../hooks/useExamesImagemSocket'
import { EstudosDicomLista } from './EstudosDicomLista'
import { OrientacaoCaptura } from './OrientacaoCaptura'
import { PedidosExameImagemLista } from './PedidosExameImagemLista'
import { SolicitarExameImagemForm } from './SolicitarExameImagemForm'
import { chaveExamesImagem, useExamesImagemAtendimento } from './useExamesImagem'
import { VisualizadorDicomModal } from './VisualizadorDicomModal'

type Props = {
  idAtendimento: number
  /** Nas aferições só os estudos recebidos aparecem: a solicitação fica no item próprio do trilho. */
  somenteResultados?: boolean
}

const ID_TITULO = 'exames-imagem-titulo'
const ID_FORM = 'exames-imagem-form'
const ID_PEDIDOS = 'exames-imagem-pedidos'
const ID_ESTUDOS = 'exames-imagem-estudos'

export const ExamesImagemSecao: FC<Props> = ({ idAtendimento, somenteResultados }) => {
  const { t } = useT()
  const queryClient = useQueryClient()
  const exames = useExamesImagemAtendimento(idAtendimento)
  const [formularioAberto, setFormularioAberto] = useState(false)
  const [resultado, setResultado] = useState<ResultadoSolicitacaoExameImagem | undefined>(undefined)
  const [estudoAberto, setEstudoAberto] = useState<EstudoDicom | undefined>(undefined)
  const fecharViewer = useCallback(() => setEstudoAberto(undefined), [])

  const recarregar = useCallback(
    () => queryClient.invalidateQueries(chaveExamesImagem(idAtendimento)),
    [queryClient, idAtendimento]
  )
  useExamesImagemSocket(idAtendimento, recarregar)

  const solicitacoes = exames.data?.solicitacoes.data ?? []
  const estudos = exames.data?.estudos ?? []
  // a orientação só vale enquanto o pedido aguarda captura
  const pedidoDoResultado = resultado
    ? solicitacoes.find((s) => s.id === resultado.solicitacao.id)
    : undefined
  const mostrarOrientacao =
    !!resultado && (!pedidoDoResultado || pedidoDoResultado.situacao === 'AGUARDANDO')

  const onSolicitado = (novo: ResultadoSolicitacaoExameImagem) => {
    setResultado(novo)
    setFormularioAberto(false)
  }

  const listas = () => {
    if (exames.isLoading) {
      return <LoadingIndicator message={t('exames_imagem.carregando')} />
    }
    if (exames.isError) {
      return (
        <InfoAlert
          variant='danger'
          role='alert'
          size='sm'
          action={
            <Button variant='tertiary' size='btn-sm' onClick={() => exames.refetch()}>
              {t('exames_imagem.tentar_novamente')}
            </Button>
          }
        >
          {t('exames_imagem.erro_carregar')}
        </InfoAlert>
      )
    }
    if (solicitacoes.length === 0 && estudos.length === 0) {
      return <p className='text-gray-700 mb-0'>{t('exames_imagem.vazio')}</p>
    }
    return (
      <>
        {!somenteResultados && solicitacoes.length > 0 && (
          <div className='mb-5'>
            <h4 id={ID_PEDIDOS} className='fs-6 fw-bold text-gray-900 mb-2'>
              {t('exames_imagem.pedidos.titulo')}
            </h4>
            <PedidosExameImagemLista
              idAtendimento={idAtendimento}
              solicitacoes={solicitacoes}
              tituloId={ID_PEDIDOS}
            />
          </div>
        )}
        {estudos.length > 0 && (
          <div>
            <h4 id={ID_ESTUDOS} className='fs-6 fw-bold text-gray-900 mb-2'>
              {t('exames_imagem.estudos.titulo')}
            </h4>
            <EstudosDicomLista
              idAtendimento={idAtendimento}
              estudos={estudos}
              solicitacoes={solicitacoes}
              tituloId={ID_ESTUDOS}
              onAbrirEstudo={setEstudoAberto}
            />
          </div>
        )}
      </>
    )
  }

  if (somenteResultados && !exames.isError && estudos.length === 0) return null

  return (
    <section
      aria-labelledby={ID_TITULO}
      className={somenteResultados ? 'border-top border-gray-300 pt-5 mt-5' : undefined}
    >
      <div className='d-flex align-items-center justify-content-between gap-4 mb-4'>
        <h3
          id={ID_TITULO}
          className={somenteResultados ? 'fs-5 fw-bold text-gray-900 mb-0' : 'visually-hidden'}
        >
          {t('exames_imagem.titulo')}
        </h3>
        {!somenteResultados && (
          <Button
            variant='secondary-outline'
            size='btn-sm'
            ariaExpanded={formularioAberto}
            ariaControls={formularioAberto ? ID_FORM : undefined}
            onClick={() => {
              // o resultado anterior sai para não ser confundido com o da nova solicitação
              if (!formularioAberto) setResultado(undefined)
              setFormularioAberto(!formularioAberto)
            }}
          >
            {t('exames_imagem.solicitar')}
          </Button>
        )}
      </div>
      {!somenteResultados && formularioAberto && (
        <SolicitarExameImagemForm
          id={ID_FORM}
          idAtendimento={idAtendimento}
          onSolicitado={onSolicitado}
        />
      )}
      {resultado && mostrarOrientacao && <OrientacaoCaptura {...resultado} />}
      {listas()}
      <VisualizadorDicomModal
        idAtendimento={idAtendimento}
        estudo={estudoAberto}
        onHide={fecharViewer}
      />
    </section>
  )
}
